'use client';

/**
 * Creating a session: the request its target picks, the rows a create seeds so the session's view
 * and the sidebar show it before any read, and the creates still in flight.
 */
import { c as _c } from "react/compiler-runtime";
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import type { CodeSessionRow, CreateCodeSessionInput, SessionResponse } from './types';
import { codeActionExcerpt } from './action-toast';
import { codeSessionKeys } from './queries';
import { cloudRuntime, codeSessionRequest, jsonBody } from './runtime/cloud';
import { startAgainThen } from './runtime/local/local-retry';
import { localRuntime } from './runtime/local/local-runtime';
import { retryToast } from './runtime/request-error';
import { listsFor } from './runtime/session-lists';

/**
 * Each create's row key, by its input, which TanStack keeps as the mutation's variables and hands
 * to its callbacks.
 */
const rowKeys = new WeakMap<CreateCodeSessionInput, string>();
let createdRows = 0;
const savedRowKeys = new Map<string, string>();
function rowKeyOf(input: CreateCodeSessionInput): string {
  let key = rowKeys.get(input);
  if (key === undefined) {
    createdRows += 1;
    key = `creating-${createdRows}`;
    rowKeys.set(input, key);
  }
  return key;
}

/** The row key of a session created on this page, which its placeholder listed under. */
export function createdRowKey(sessionId: string): string | undefined {
  return savedRowKeys.get(sessionId);
}

/**
 * Show a session the server just created without waiting for a detail or list read, with what
 * the runtime that created it already knows of the read's answer (`created`).
 */
export async function seedNewSession(client: QueryClient, created: Pick<SessionResponse, 'session' | 'imageRoom' | 'setupEvents'>) {
  const {
    session
  } = created;
  client.setQueryData<SessionResponse>(codeSessionKeys.detail(session.id), existing => existing ?? {
    transcript: null,
    events: [],
    cursor: null,
    resuming: false,
    mcpToolsRunAs: null,
    ...created
  });
  await listsFor(session.id).add(client, session);
}

/**
 * Create the session: its target picks the runtime; for one that sets up the environment, the
 * server picks what it starts with.
 */
function createCodeSession({
  target,
  model,
  prompt
}: CreateCodeSessionInput): Promise<Pick<SessionResponse, 'session' | 'imageRoom' | 'setupEvents'>> {
  if (target.kind === 'local') {
    // A folder on this computer: its runtime creates the session with its first prompt.
    return localRuntime.createSession({
      folder: target.folder.path,
      model,
      prompt,
      attachments: target.attachments,
      approvalMode: target.approvalMode
    });
  }
  const {
    installationId,
    repositoryId,
    attachments,
    warmSessionId,
    environmentSetup
  } = target;
  if (environmentSetup) {
    return codeSessionRequest<{
      session: CodeSessionRow;
    }>('/api/code-sessions/environment-setup', jsonBody({
      installationId,
      repositoryId,
      ...(environmentSetup.runId === null ? {} : {
        runId: environmentSetup.runId
      })
    }));
  }
  return cloudRuntime.createSession({
    installationId,
    repositoryId,
    model,
    prompt,
    attachments,
    warmSessionId
  });
}
export function useCreateCodeSession(t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onRetry
  } = t1;
  const client = useQueryClient();
  let t2;
  if ($[2] !== client) {
    t2 = (created, input) => {
      savedRowKeys.set(created.session.id, rowKeyOf(input));
      return seedNewSession(client, created);
    };
    $[2] = client;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onRetry) {
    t3 = (error, input_0) => {
      const {
        prompt,
        target
      } = input_0;
      const place = target.kind === "cloud" ? target.repoFullName : target.folder.name;
      const retry = target.kind === "local" && onRetry !== undefined ? retryToast(error, () => startAgainThen("code-session-create-retry", () => onRetry(input_0))) : {};
      toast.error("Could not create session", {
        description: `${codeActionExcerpt(prompt)} · ${place}\n${error.message}`,
        descriptionClassName: "whitespace-pre-line",
        ...retry
      });
    };
    $[4] = onRetry;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      mutationKey: codeSessionKeys.create,
      retry: false,
      mutationFn: createCodeSession,
      onSuccess: t2,
      onError: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}

/** The creates in flight, newest first, so one that answers before a newer one keeps its place. */
export function usePendingSessionCreations() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.create,
        status: "pending"
      },
      select: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const pending = useMutationState(t0);
  let t1;
  if ($[1] !== pending) {
    t1 = pending.toReversed();
    $[1] = pending;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp(mutation) {
  const input = mutation.state.variables as CreateCodeSessionInput;
  return {
    id: rowKeyOf(input),
    input
  };
}